<template>
    <!-- IE 2026 09 25 / ODOO-341407 (замечание пользователей через Игоря: «не всегда понятно, когда переключение выполнено полностью, включая
         обновление выведенных элементов»): ход живого переключения языка — «Switching language… Widgets updated: N of M» → «Language switched:
         Українська (X s)»; ошибки и оставленные виджеты — строками ниже; виджеты других страниц — «обновятся при открытии». Состояние —
         relocalize.switchProgress (завершение = закончены ВСЕ перезапросы данных активной страницы и словари, используемые на ней).
         Выключатель: window.i18nSwitch.set('switchProgress', false) -->
    <div v-if="p.visible" class="i18n-switch-progress" :class="{ 'isp-done': !p.active, 'isp-errors': hasErrors }" role="status" aria-live="polite">
        <div class="isp-bar" v-if="p.active"><div class="isp-bar-fill" :style="{ width: barPct + '%' }"></div></div>
        <i :class="['pi', 'isp-icon', p.active ? 'pi-spin pi-spinner' : (hasErrors ? 'pi-exclamation-triangle' : 'pi-check-circle')]"></i>
        <div class="isp-body">
            <div class="isp-title">{{ title }}</div>
            <div v-if="p.active" class="isp-line">{{ activeLine }}</div>
            <div v-for="(line, i) in notes" :key="i" class="isp-line" :class="{ 'isp-error': line.error }">{{ line.text }}</div>
        </div>
        <div class="isp-close" :title="t('Close')" @click="hide"><i class="pi pi-times"></i></div>
    </div>
</template>

<script>
import { switchProgress, switchProgressHide } from '@/i18n/relocalize'
import { getUiLocale } from '@/i18n'

export default {
    name: 'I18nSwitchProgress',
    setup() {
        return { p: switchProgress }
    },
    computed: {
        hasErrors() { return !!(this.p.failed || this.p.error || this.p.stillLoading) },
        seconds() {   // секунды с одним знаком — разделитель по языку интерфейса (27,5 с / 27.5 s)
            const v = Math.round((this.p.ms || 0) / 100) / 10
            try { return new Intl.NumberFormat(getUiLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v) } catch (e) { return v.toFixed(1) }
        },
        title() {
            const lang = this.p.langTitle || this.p.lang
            if (this.p.active) return this.p.mode === 'activation' ? this.t('Updating the page to {lang}…', { lang }) : this.t('Switching language to {lang}…', { lang })
            if (this.p.mode === 'activation') return this.hasErrors ? this.t('Page updated with errors: {lang} ({s} s)', { lang, s: this.seconds }) : this.t('Page updated: {lang} ({s} s)', { lang, s: this.seconds })
            return this.hasErrors ? this.t('Language switched with errors: {lang} ({s} s)', { lang, s: this.seconds }) : this.t('Language switched: {lang} ({s} s)', { lang, s: this.seconds })
        },
        activeLine() {
            if (this.p.total && this.p.done < this.p.total) return this.t('Widgets updated: {n} of {total}', { n: this.p.done, total: this.p.total })
            if (this.p.phase === 'ui' || this.p.phase === 'labels') return this.t('Updating labels…')
            if (this.p.dictsBusy) return this.t('Updating reference titles…')
            return this.p.total ? this.t('Widgets updated: {n} of {total}', { n: this.p.done, total: this.p.total }) : this.t('Updating labels…')
        },
        barPct() {
            // подписи — первые 20 %, данные — до 90 %, словари — остаток
            const labels = this.p.phase === 'ui' || this.p.phase === 'labels' ? 5 : 20
            const data = this.p.total ? 70 * this.p.done / this.p.total : 70
            return Math.max(5, Math.min(100, labels + (this.p.phase === 'ui' || this.p.phase === 'labels' ? 0 : data) + (this.p.phase === 'done' ? 10 : 0)))
        },
        notes() {
            const out = []
            const p = this.p
            if (p.error) out.push({ text: p.error, error: true })
            if (p.failed) out.push({ text: this.tn(p.failed, '{n} widget failed to update', '{n} widgets failed to update') + (p.failedTitles?.length ? ': ' + p.failedTitles.join(', ') : ''), error: true })
            if (p.stillLoading) out.push({ text: this.tn(p.stillLoading, '{n} widget is still loading', '{n} widgets are still loading'), error: true })
            if (p.kept && p.keptForm) out.push({ text: this.tn(p.kept + p.keptForm, '{n} widget keeps unsaved edits or an open row form — its data labels update on the next refresh', '{n} widgets keep unsaved edits or open row forms — their data labels update on the next refresh') })
            else if (p.kept) out.push({ text: this.tn(p.kept, '{n} widget keeps unsaved edits — its data labels update on the next refresh', '{n} widgets keep unsaved edits — their data labels update on the next refresh') })
            else if (p.keptForm) out.push({ text: this.tn(p.keptForm, '{n} widget with an open row form was not refreshed — its data labels update on the next refresh', '{n} widgets with open row forms were not refreshed — their data labels update on the next refresh') })
            if (p.hidden) out.push({ text: this.tn(p.hidden, '{n} hidden widget updates when restored', '{n} hidden widgets update when restored') })
            if (p.otherPages && !p.active) out.push({ text: this.tn(p.otherPages, '{n} widget on other pages updates when its page is opened', '{n} widgets on other pages update when their pages are opened') })
            return out
        },
    },
    methods: {
        hide() { switchProgressHide() },
    },
}
</script>

<style scoped>
.i18n-switch-progress {
    position: fixed;
    left: 50%;
    bottom: 40px;
    transform: translateX(-50%);
    z-index: 1002;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 260px;
    max-width: min(560px, calc(100vw - 32px));
    padding: 8px 30px 8px 12px;
    border-radius: 0.5rem;
    border: 1px solid rgba(0, 0, 0, .18);
    background-color: #eef3fb;
    color: #4a5a6a;
    font-size: 13px;
    line-height: 1.3;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    overflow: hidden;
}
.i18n-switch-progress.isp-done { background-color: #daecda; }
.i18n-switch-progress.isp-errors { background-color: #fff1e0; }
.isp-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: rgba(0, 0, 0, 0.08);
}
.isp-bar-fill {
    height: 100%;
    background-color: #3b7ddd;
    transition: width 0.4s ease;
}
.isp-icon { margin-top: 2px; font-size: 14px; }
.isp-done .isp-icon { color: #26852b; }
.isp-errors .isp-icon { color: #c0620e; }
.isp-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.isp-title { font-weight: 600; color: #3d4b59; }
.isp-line { word-break: break-word; }
.isp-error { color: #a94400; }
.isp-close {
    position: absolute;
    top: 6px;
    right: 8px;
    cursor: pointer;
    font-size: 11px;
    color: #8a96a3;
}
.isp-close:hover { color: #333; }
</style>
